import type { Moment } from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getRemainTime, momentTimezone } from 'src/utils/time';

interface RemainTime {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

const initialRemainTime: RemainTime = {
  days: '00',
  hours: '00',
  minutes: '00',
  seconds: '00',
};

const useRemainTimer = (): {
  remainTime: RemainTime;
  startTimer: ({ endTime, intervalTime }: { endTime: Moment; intervalTime?: number }) => void;
  endTimer: () => void;
  isEndTimer: boolean;
} => {
  const [remainTime, setRemainTime] = useState<RemainTime>(initialRemainTime);
  const [isEndTimer, setIsEndTimer] = useState(false);
  const remainTimeIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const clearTimer = useCallback(() => {
    if (remainTimeIntervalRef.current) {
      clearInterval(remainTimeIntervalRef.current);
    }
  }, []);

  const endTimer = useCallback(() => {
    setRemainTime(initialRemainTime);
    setIsEndTimer(true);
    clearTimer();
  }, [clearTimer]);

  const startTimer = useCallback(
    ({ endTime, intervalTime = 500 }: { endTime: Moment; intervalTime?: number }) => {
      setIsEndTimer(false);
      clearTimer();

      const updateTimer = () => {
        const [seconds, minutes, hours, days] = getRemainTime(momentTimezone(), endTime, 'days');
        setRemainTime({ days, hours, minutes, seconds });

        if (seconds === '00' && minutes === '00' && hours === '00' && days === '00') {
          endTimer();
        }
      };

      updateTimer();

      remainTimeIntervalRef.current = setInterval(updateTimer, intervalTime);
    },
    [clearTimer, endTimer],
  );

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  return { remainTime, startTimer, endTimer, isEndTimer };
};

export default useRemainTimer;
